/**
 * @file brand-features.ts
 * @description Catálogo canônico das funcionalidades de uma marca.
 *
 * ## Por que este arquivo existe
 *
 * `enabledFeatures` era `z.array(z.string())` — sem enum, sem validação. A
 * lista fechada de valores existia apenas num `<MultiSelect>` do admin
 * (`PartnerForm.tsx`), o que produziu três problemas reais:
 *
 *   1. Um `PATCH` direto na API gravava qualquer string, inclusive uma
 *      dependência que o form dizia ser obrigatória (`CHAT_ATTACHMENTS` sem
 *      `PARTNER_CRM`/`PARTNER_PROVIDERS`).
 *   2. Um valor órfão foi parar no seeder — `PROVIDER_CHAT`, que ninguém lê
 *      (o consumidor real é `PARTNER_PROVIDERS`). O parceiro "com tudo ligado"
 *      estava, na prática, sem chat de prestadores.
 *   3. Não havia como o mantenedor descobrir o que existe sem ler o código.
 *
 * ## A régua: capacidade × concessão
 *
 * Uma **capacidade** responde "o que esta marca *pode fazer*" e mora aqui.
 * Uma **concessão** responde "a que acervo *de outra marca* ela tem acesso" e
 * mora em `grant.schema.ts` — não é funcionalidade e não aparece neste
 * catálogo.
 *
 * Foi essa régua que tirou daqui o acesso à rede e ao acervo da Eva: os dois
 * eram privilégios implícitos de todo parceiro, e viraram grants explícitos e
 * revogáveis.
 */

import { z } from 'zod';

/**
 * Funcionalidades que uma marca pode ter habilitadas.
 *
 * Ao adicionar uma: declare aqui, descreva em `BRAND_FEATURES` e o admin passa
 * a oferecê-la sozinho — a tela lê deste catálogo, não de uma lista própria.
 */
export const BrandFeatureEnum = z.enum([
  /** Painéis de métricas e engajamento no admin da marca. */
  'ANALYTICS_DASHBOARD',
  /** Rede de profissionais própria: perfis, acessos e chat com pacientes. */
  'PARTNER_PROVIDERS',
  /** Time de CRM próprio, com chat e o preset de permissão correspondente. */
  'PARTNER_CRM',
  /** Catálogo de conteúdo próprio: CMS, trilhas e o preset de permissão. */
  'CUSTOM_CONTENT',
  /**
   * O chat inteiro da marca (A053): a aba da paciente, a caixa do painel e
   * do profissional, e a API de conversa. Sem ela, nenhuma casa da política
   * de chat acende. Parceiro novo nasce sem; as marcas que já existiam
   * receberam por backfill, porque todas conversavam antes do interruptor.
   */
  'CHAT',
  /** Anexos nas conversas de chat. */
  'CHAT_ATTACHMENTS',
  /** Agendamento de consultas via Calendly. */
  'CALENDLY_SCHEDULING',
  /** Integração com a comunidade Cativa (login, feed e cadastro externos). */
  'CATIVA_INTEGRATION',
  /**
   * Ter empresas como clientes (A062): cadastrar empresas e vincular as
   * colaboradoras a elas. É a base; relatórios e portal da empresa são
   * aprofundamentos, cada um uma funcionalidade à parte.
   */
  'COMPANY_MANAGEMENT',
  /**
   * Portal da empresa (A062): a gestora indicada pela marca (o RH) entra em
   * `/empresa` e acompanha o acesso das colaboradoras. Sem esta
   * funcionalidade, a marca administra as empresas sozinha e a sessão de
   * gestora não alcança nada. Desligada por padrão; o mantenedor liga por
   * marca, conforme o que ela contratou.
   */
  'COMPANY_PORTAL',
  /**
   * Pacientes (A062, fase D): a equipe da marca vê e atende as próprias
   * pacientes — lista, ficha, perfil clínico, respostas de cuidado. Toda marca
   * de atendimento tem; a marca corporativa (uma empresa que é marca) nasce
   * sem, e quem atende as colaboradoras dela é outra marca, por concessão.
   *
   * Ausente = desligada, e o servidor fecha: não é só o menu. Por isso as
   * marcas existentes ganham a funcionalidade por backfill antes do deploy
   * (docs/migracoes/2026-10-funcionalidade-pacientes.md).
   */
  'PATIENT_DIRECTORY',
  /** Painéis com números fictícios, para demonstração comercial. */
  'DEMO_DASHBOARDS',
  /**
   * Jornadas de cuidado — Checkpoints & Lembretes (A043). Liga o construtor
   * de cuidado (e registros, variáveis, ferramentas) no admin da marca, a
   * aba Cuidado no app da paciente e a aba Cuidado no builder de telas.
   * Desligada por padrão: é produto que a marca contrata.
   */
  'CARE_JOURNEYS',

  /**
   * Agente de jornadas (plano "Agente de Jornadas", 25/09/2026): um agente
   * de IA no admin que lê a documentação clínica e monta checkpoints e
   * lembretes com o catálogo da marca. Entrega proposta para revisão; quem
   * grava é o importador, sempre como rascunho, e nada é criado com
   * pendência aberta. Desligada por padrão; o mantenedor liga por marca.
   */
  'CARE_JOURNEY_AGENT',

  /**
   * Revistas próprias: a marca escreve as revistas dela (gestacional por
   * semana, pós-parto, por tópico) no painel. Sem a funcionalidade, a marca
   * só recebe revistas por concessão — que é o caso de todas hoje, menos a
   * Eva (fase 8 do plano "A Eva deixa de ser especial"). Desligada por
   * padrão.
   */
  'MAGAZINE_AUTHORING',

  /**
   * Textos das ferramentas de cuidado (A059): a marca escreve a abertura de
   * cada ferramenta no painel. O catálogo de ferramentas é um só, sem
   * dimensão de marca — o texto vale para as pacientes de todas as marcas.
   * Por isso é funcionalidade e não papel: quem escreve para o ecossistema
   * é quem o mantenedor habilitou. Hoje, a Eva. Desligada por padrão.
   */
  'CARE_TOOL_AUTHORING',

  /**
   * Telas técnicas do painel (A155): Variáveis e Registros, em Jornadas;
   * Auditoria de uploads, em Conteúdo. Exceções por paciente ficou com a
   * lista branca (`CONTENT_WHITELIST`, A107). Servem a
   * quem opera o sistema e confundem quem só usa o painel, por isso o
   * parceiro não as vê. Funcionalidade e não `isEvaBrand`, pela A046: quem
   * vê é a marca que o mantenedor habilitou. Hoje, a Eva. Desligada por
   * padrão.
   */
  'ADMIN_TECHNICAL_PAGES',

  /**
   * Lista branca de conteúdo (A107): em Conteúdo → Exceções por paciente, a
   * administração da marca restringe o acervo — cada paciente só vê o que for
   * liberado um a um para ela.
   *
   * Era um interruptor aberto a toda marca, sem funcionalidade. Em 07/10/2026
   * foi ligado por engano na Eva Saúde e no ILP, sem nenhuma paciente com
   * curso liberado, e os conteúdos sumiram para 137 pacientes. Sem a
   * funcionalidade, a tela não aparece, a API recusa ligar, e o filtro
   * ignora o interruptor. Desligada por padrão; quem já usava recebeu por
   * backfill (docs/migracoes/2026-10-lista-branca-vira-funcionalidade.md).
   */
  'CONTENT_WHITELIST',

  /**
   * Transcrição ao vivo (A135): a paciente fala e o texto aparece no diário.
   * Consome um pool mensal de minutos dividido entre todas as marcas que têm
   * a funcionalidade, com limite diário por pessoa — os dois configurados
   * pelo mantenedor na chave desta funcionalidade. Desligada, o microfone não
   * aparece. Desligada por padrão.
   */
  'TRANSCRICAO',

  /**
   * Eventos (A143, 11/10/2026): a marca cadastra rodas de conversa e lives na
   * página Eventos, divulga no card da home, e a paciente se inscreve e vê na
   * agenda dela. Sem a funcionalidade, a página e o submenu somem, a API da
   * marca recusa o cadastro, a da paciente não lista nem inscreve, e os blocos
   * de evento não aparecem no app nem no builder. Desligada por padrão; quem
   * já usava recebeu por backfill (docs/migracoes/2026-10-eventos-vira-funcionalidade.md).
   */
  'EVENTOS',

  /**
   * Integração com o ActiveCampaign (A172, 15/10/2026): a administração da
   * marca conecta a conta (URL e chave de API, em Configurações) e escolhe que
   * formulário vira contato e negócio em que funil do CRM, com o mapa dos
   * campos. Sem a funcionalidade, o menu some, a API recusa a configuração e
   * nenhuma resposta é enviada. Desligada por padrão.
   */
  'ACTIVECAMPAIGN',

  /**
   * @deprecated Virou a concessão `PROVIDER_NETWORK` da marca Eva.
   *
   * Continua no enum durante a janela de migração para que documentos ainda não
   * migrados validem. O código não deve mais consultá-la: quem responde
   * "esta marca alcança a rede da Eva?" é o `GrantService`.
   */
  'EVA_PROVIDERS',
]);

export type BrandFeature = z.infer<typeof BrandFeatureEnum>;

/** Descrição de uma funcionalidade, consumida pelo admin para montar a tela. */
export interface BrandFeatureMeta {
  /** Rótulo exibido ao mantenedor. */
  label: string;
  /** O que a marca ganha ao habilitar. */
  description: string;
  /**
   * Funcionalidades que precisam estar ligadas junto.
   *
   * A validação vive no schema (ver `validateBrandFeatures`), não no form —
   * era exatamente por morar só no form que dava para burlar por API.
   */
  requires?: BrandFeature[];
  /**
   * Funcionalidades que precisam estar TODAS ligadas — um "e", ao contrário
   * de `requires`, que é um "ou". As duas convivem: anexos exigem o chat E
   * (CRM OU rede própria).
   */
  requiresAll?: BrandFeature[];
  /**
   * Preenchido quando a funcionalidade está saindo do catálogo. O texto explica
   * para onde a governança foi.
   */
  deprecated?: string;
  /**
   * Marca a funcionalidade como derivada de configuração, não escolhida à mão.
   * O admin mostra como somente-leitura.
   */
  derivedFrom?: string;
}

export const BRAND_FEATURES: Record<BrandFeature, BrandFeatureMeta> = {
  ANALYTICS_DASHBOARD: {
    label: 'Painéis de métricas',
    description: 'Dashboards de engajamento e uso no admin da marca.',
  },
  PARTNER_PROVIDERS: {
    label: 'Rede de profissionais própria',
    description:
      'A marca cadastra os próprios profissionais e eles atendem as usuárias(os) dela.',
  },
  PARTNER_CRM: {
    label: 'Time de CRM',
    description: 'Equipe própria de atendimento, com acesso ao chat com usuárias(os).',
  },
  CUSTOM_CONTENT: {
    label: 'Catálogo de conteúdo próprio',
    description:
      'A marca publica vídeos, revistas e trilhas próprios, além do que recebe por concessão.',
  },
  CHAT: {
    label: 'Chat',
    description:
      'Conversas por mensagem: a usuária(o) com o time de CRM e com os profissionais, e a equipe entre si. Desligado, o chat some do app, do painel e do portal do profissional; as conversas antigas ficam guardadas e voltam se religar.',
  },
  CHAT_ATTACHMENTS: {
    label: 'Anexos no chat',
    description: 'Permite enviar arquivos nas conversas.',
    // Anexo só faz sentido onde existe conversa — e conversa depende de o
    // chat estar ligado e de ter time de CRM ou rede de profissionais.
    requiresAll: ['CHAT'],
    requires: ['PARTNER_CRM', 'PARTNER_PROVIDERS'],
  },
  CALENDLY_SCHEDULING: {
    label: 'Agendamento (Calendly)',
    description: 'Agendamento de consultas com a agenda conectada da marca.',
  },
  CATIVA_INTEGRATION: {
    label: 'Integração Cativa',
    description: 'Login, feed da comunidade e cadastro vindos da plataforma Cativa.',
    derivedFrom: 'cativaConfig',
  },
  COMPANY_MANAGEMENT: {
    label: 'Carteira de empresas',
    description:
      'A marca atende empresas: cadastra cada uma e vincula as colaboradoras a ela. Quem administra é a própria marca; o portal para o RH da empresa é outra funcionalidade.',
  },
  COMPANY_PORTAL: {
    label: 'Portal da empresa',
    description:
      'O RH de cada empresa entra num portal próprio e acompanha o acesso das colaboradoras, sem ver dado de saúde. A gestora é indicada pela marca.',
    requires: ['COMPANY_MANAGEMENT'],
  },
  PATIENT_DIRECTORY: {
    label: 'Usuárias(os)',
    description:
      'A equipe da marca vê e atende as próprias usuárias(os): lista, ficha, perfil clínico e respostas de cuidado. Desligada, ninguém da marca alcança dado de usuária(o), nem pela API — é o caso da marca corporativa, atendida por outra marca.',
  },
  DEMO_DASHBOARDS: {
    label: 'Painéis de Métricas Fictícias (Demo)',
    description:
      'Financeiro & ROI, RH e Saúde com números de demonstração, e Engajamento com uma amostra pronta. Serve para apresentação comercial: os três primeiros não têm cálculo real hoje, e os números não vêm desta marca.',
    // Painel fictício sem painel nenhum não faz sentido: a demonstração
    // convive com o Engajamento real, que é o que prova que o produto mede.
    requires: ['ANALYTICS_DASHBOARD'],
  },
  CARE_JOURNEYS: {
    label: 'Jornadas de cuidado (Checkpoints & Lembretes)',
    description:
      'Construtor de cuidado, registros, variáveis e ferramentas no admin; aba Cuidado no app da usuária(o) e no builder de telas.',
  },
  CARE_JOURNEY_AGENT: {
    label: 'Agente de jornadas (IA)',
    description:
      'Cria checkpoints e lembretes a partir de documentação ou de uma descrição, usando o catálogo da marca. Guia a resolução de pendências e entrega rascunho para revisão; não publica.',
    // O agente monta itens do construtor de cuidado: sem jornadas, não há
    // onde o resultado morar.
    requires: ['CARE_JOURNEYS'],
  },
  CARE_TOOL_AUTHORING: {
    label: 'Textos das ferramentas de cuidado',
    description:
      'A marca escreve o texto de abertura das ferramentas (geral e por jornada), em Jornadas → Ferramentas. O catálogo é um só: o texto aparece para as usuárias(os) de todas as marcas.',
    // A tela fica dentro das jornadas de cuidado: sem elas, não há onde editar.
    requires: ['CARE_JOURNEYS'],
  },
  ADMIN_TECHNICAL_PAGES: {
    label: 'Telas técnicas do painel',
    description:
      'Variáveis e Registros (em Jornadas) e Auditoria de uploads (em Conteúdo). Para quem opera o sistema. Variáveis é catálogo único: o que a marca cria ou muda vale para todas.',
  },
  CONTENT_WHITELIST: {
    label: 'Lista branca de conteúdo',
    description:
      'Em Conteúdo → Exceções por paciente, a administração da marca restringe o acervo: cada usuária(o) só vê os cursos e podcasts liberados um a um para ela. Sem isto, a marca só oculta itens do que recebe.',
  },
  TRANSCRICAO: {
    label: 'Transcrição por voz',
    description:
      'A usuária(o) fala e o texto vai aparecendo no diário. Os minutos saem de um pool mensal dividido entre todas as marcas com esta funcionalidade; o áudio não é guardado.',
    requires: ['CARE_JOURNEYS'],
  },
  EVENTOS: {
    label: 'Eventos',
    description:
      'A marca cadastra rodas de conversa e lives, divulga na home do app, e a usuária(o) se inscreve e acompanha na agenda dela. A equipe vê a lista de inscritas.',
  },
  ACTIVECAMPAIGN: {
    label: 'ActiveCampaign',
    description:
      'A marca conecta a conta do ActiveCampaign e escolhe que formulário do app vira contato e negócio em que funil do CRM, e com quais campos.',
  },
  MAGAZINE_AUTHORING: {
    label: 'Revistas próprias',
    description:
      'A marca escreve as próprias revistas no painel (gestacional por semana, pós-parto e por tópico). Sem isto, só recebe revistas por concessão.',
  },
  EVA_PROVIDERS: {
    label: 'Rede Eva (legado)',
    description: 'Acesso à rede de profissionais da Eva.',
    deprecated:
      'Virou a concessão PROVIDER_NETWORK da marca Eva. Consulte o GrantService, não esta flag.',
  },
};

/**
 * Funcionalidades que o mantenedor escolhe à mão, na ordem em que a tela lista.
 *
 * Exclui as depreciadas (governadas por concessão) e as derivadas de
 * configuração — nenhuma das duas é decisão de checkbox.
 */
export const SELECTABLE_BRAND_FEATURES: BrandFeature[] = (
  Object.keys(BRAND_FEATURES) as BrandFeature[]
).filter((f) => !BRAND_FEATURES[f].deprecated && !BRAND_FEATURES[f].derivedFrom);

/**
 * Campo `enabledFeatures` do documento de marca.
 *
 * Descarta silenciosamente valores desconhecidos em vez de rejeitar o
 * documento. É deliberado: `BaseRepository.findMany` lança no primeiro
 * documento inválido, então um valor órfão gravado no passado (o
 * `PROVIDER_CHAT` do seeder) derrubaria a listagem inteira de parceiros — um
 * dado sujo viraria um incidente. Descartar preserva o resto do documento e o
 * valor some na primeira gravação.
 */
export const brandFeaturesField = () =>
  z.preprocess((raw) => {
    if (!Array.isArray(raw)) return raw;
    const validos = BrandFeatureEnum.options as readonly string[];
    return raw.filter((f): f is string => typeof f === 'string' && validos.includes(f));
  }, z.array(BrandFeatureEnum).optional());

/**
 * Verifica as dependências entre funcionalidades.
 *
 * `requires` é satisfeito por QUALQUER uma da lista (é um "ou"), que é como o
 * form do admin já se comportava para `CHAT_ATTACHMENTS`. `requiresAll` exige
 * todas; cada uma que falta vira um problema próprio.
 *
 * Devolve a lista de problemas em vez de lançar, para que o chamador decida:
 * a API rejeita a escrita, o admin pinta o campo, e uma leitura de documento
 * legado apenas registra.
 */
export function validateBrandFeatures(features: BrandFeature[]): string[] {
  const problemas: string[] = [];
  const habilitadas = new Set(features);

  for (const feature of features) {
    const meta = BRAND_FEATURES[feature];

    for (const dep of meta?.requiresAll ?? []) {
      if (!habilitadas.has(dep)) {
        problemas.push(`"${meta.label}" exige ${BRAND_FEATURES[dep].label}.`);
      }
    }

    const requires = meta?.requires;
    if (!requires?.length) continue;

    if (!requires.some((dep) => habilitadas.has(dep))) {
      const nomes = requires.map((d) => BRAND_FEATURES[d].label).join(' ou ');
      problemas.push(
        `"${meta.label}" exige ${nomes}.`
      );
    }
  }

  return problemas;
}

/**
 * As dependências desta funcionalidade estão satisfeitas na lista?
 *
 * `requires` é um "ou": qualquer uma das listadas basta. Existe para a tela
 * poder desabilitar a opção em vez de deixar marcar e recusar no salvamento
 * — e para fazer isso a partir do CATÁLOGO, não de um `if` por funcionalidade.
 *
 * Era esse `if` que existia: os dois formulários de marca perguntavam
 * literalmente por `CHAT_ATTACHMENTS`, então `DEMO_DASHBOARDS` nasceu com
 * dependência declarada e sem ninguém para respeitá-la na interface.
 */
export function dependenciasSatisfeitas(
  feature: BrandFeature,
  habilitadas: readonly string[]
): boolean {
  const meta = BRAND_FEATURES[feature];
  if (!(meta?.requiresAll ?? []).every((dep) => habilitadas.includes(dep))) return false;
  const requires = meta?.requires;
  if (!requires?.length) return true;
  return requires.some((dep) => habilitadas.includes(dep));
}

/** A marca tem a funcionalidade habilitada? */
export function hasBrandFeature(
  features: readonly string[] | null | undefined,
  feature: BrandFeature
): boolean {
  return Boolean(features?.includes(feature));
}
